import { useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { leerVolverA } from '../utils/volverA';

/**
 * El "volver" de una pantalla que puede haberse abierto desde CUALQUIER lado:
 * `navigate(-1)` cuando hay historial in-app, y una ruta fija sólo como red.
 *
 * Es el criterio de la casa desde ADR-144 (Plantillas), acá factorizado —
 * ADR-2026-09-07-el-back-de-los-modos-vuelve-a-donde-estabas. `location.key !== 'default'`
 * significa que a esta pantalla se llegó navegando dentro de la app, así que abajo hay
 * una entrada nuestra y el POP devuelve a la pantalla real de origen. `'default'` es la
 * PRIMERA entrada del router —deep-link, refresh, link compartido—: ahí no hay nada
 * nuestro atrás y un `navigate(-1)` a ciegas sacaría al usuario de la app.
 *
 * Nunca `window.history.length`: cuenta toda la sesión de la pestaña, así que da > 1
 * también cuando se entró por deep-link (ADR-793).
 *
 * Si quien abrió esta pantalla dijo a dónde volver (`volverA` en el state,
 * ADR-2026-09-16-volver-a-donde-vine), eso manda sobre las dos cosas: es la pantalla de
 * origen dicha explícitamente, y no depende de cómo quedó el historial.
 *
 * @param destinoSinHistorial A dónde ir cuando no hay historial propio. Por defecto la
 *   home (`/interfaz-v2`), que es la salida segura de cualquier pantalla de la app.
 */
export function useVolverAtras(destinoSinHistorial = '/interfaz-v2'): () => void {
  const navigate = useNavigate();
  const location = useLocation();
  // Memo por `location.state` (estable entre renders): el callback conserva su identidad,
  // que los modos meten en efectos (`setMobileBackOverride`).
  const volverA = useMemo(() => leerVolverA(location.state), [location.state]);
  return useCallback(() => {
    if (volverA) navigate(volverA.path, { state: volverA.state });
    else if (location.key !== 'default') navigate(-1);
    else navigate(destinoSinHistorial);
  }, [volverA, location.key, navigate, destinoSinHistorial]);
}

export default useVolverAtras;
